import { createFileRoute, Link } from "@tanstack/react-router";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { ChevronRight, ShieldCheck, Zap, Home, Wrench } from "lucide-react";

const SITE_URL = "https://elvion-group.nl";
const URL = `${SITE_URL}/info/meterkast-vervangen-kosten`;

const FAQS = [
  {
    q: "Wat kost een meterkast vervangen gemiddeld?",
    a: "Een complete meterkast vervangen kost bij een gemiddelde woning tussen € 850 en € 2.250 inclusief materiaal, arbeid en NEN 1010-meetrapport. De prijs hangt af van het aantal groepen, 1- of 3-fase, of er krachtstroom nodig is en of de meterkast-behuizing zelf ook vervangen wordt.",
  },
  {
    q: "Wanneer moet ik mijn meterkast vervangen?",
    a: "Vervangen is nodig bij overschakelen naar 3-fase / krachtstroom (elektrisch koken, warmtepomp, laadpaal), bij een verouderde kast zonder aardlekautomaten, bij te weinig groepen voor moderne apparatuur, of wanneer een verzekeraar of taxateur een keuring afkeurt.",
  },
  {
    q: "Mag ik zelf mijn meterkast vervangen?",
    a: "Nee. Werkzaamheden aan de hoofdaansluiting en groepenkast moeten worden uitgevoerd door een erkend installateur volgens NEN 1010. Alleen dan is de installatie verzekerd en krijgt u een geldig meetrapport.",
  },
  {
    q: "Hoe lang duurt het vervangen van een meterkast?",
    a: "Bij een woning duurt het vervangen van de groepenkast doorgaans 1 werkdag. Verzwaren naar 3-fase kost extra tijd omdat de netbeheerder de aansluiting moet aanpassen — reken op 2 tot 6 weken doorlooptijd voor de aanvraag.",
  },
  {
    q: "Wat is het verschil tussen 1-fase en 3-fase?",
    a: "1-fase (1×25A of 1×35A) is standaard in oudere woningen en levert ongeveer 5,7 tot 8 kW. 3-fase / krachtstroom (3×25A of 3×35A) levert 17 tot 24 kW en is nodig voor snelladen, inductiekoken en warmtepompen die tegelijk draaien.",
  },
];

export const Route = createFileRoute("/info/meterkast-vervangen-kosten")({
  head: () => ({
    meta: [
      { title: "Meterkast vervangen kosten 2026 | Elvion Group Nederland helpt u" },
      {
        name: "description",
        content:
          "Meterkast vervangen kosten: prijzen per situatie, wanneer verzwaren naar 3-fase nodig is en hoe Elvion Group Nederland u graag verder helpt met een NEN 1010-oplevering.",
      },
      { property: "og:title", content: "Meterkast vervangen — kosten & proces | Elvion Group Nederland" },
      {
        property: "og:description",
        content:
          "Wat kost meterkast vervangen? Complete gids over 1- en 3-fase, verzwaren en NEN 1010. Elvion Group Nederland helpt u graag verder met erkende installateurs.",
      },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { property: "og:locale", content: "nl_NL" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "Meterkast vervangen — kosten, proces en NEN 1010",
          author: { "@type": "Organization", name: "Elvion Group Nederland" },
          publisher: { "@type": "Organization", name: "Elvion Group Nederland" },
          mainEntityOfPage: URL,
          inLanguage: "nl-NL",
        }),
      },
    ],
  }),
  component: MeterkastPage,
});

function MeterkastPage() {
  return (
    <div className="min-h-screen bg-background">
      <Header variant="shop" />
      <main className="mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="Kruimelpad" className="mb-4 text-sm text-text-secondary">
          <Link to="/" className="hover:underline">Home</Link>
          <ChevronRight className="inline size-3 mx-1" />
          <span>Meterkast vervangen kosten</span>
        </nav>

        <h1 className="text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
          Meterkast vervangen: kosten, proces en NEN 1010
        </h1>
        <p className="mt-4 text-lg leading-relaxed text-text-secondary">
          Overweegt u de meterkast te laten vervangen bij een verbouwing, de overstap naar
          elektrisch koken, een warmtepomp of het plaatsen van een laadpaal? Op deze pagina
          leest u wat een nieuwe meterkast kost, wanneer verzwaren naar 3-fase / krachtstroom
          nodig is en hoe onze erkende installateurs de installatie volgens NEN 1010 opleveren.
        </p>

        <div className="mt-8 grid gap-4 sm:grid-cols-3">
          {[
            { icon: ShieldCheck, title: "NEN 1010", text: "Meetrapport bij oplevering" },
            { icon: Zap, title: "1- of 3-fase", text: "Advies over verzwaren" },
            { icon: Wrench, title: "Vaste prijs", text: "Vooraf schriftelijk vastgelegd" },
          ].map((b) => (
            <div key={b.title} className="rounded-2xl border border-border bg-card p-5 shadow-sm">
              <b.icon className="size-6 text-[var(--sky)]" aria-hidden />
              <h3 className="mt-3 font-bold text-foreground">{b.title}</h3>
              <p className="mt-1 text-sm text-text-secondary">{b.text}</p>
            </div>
          ))}
        </div>

        <section className="mt-10">
          <h2 className="text-2xl font-bold text-foreground">Wat kost een meterkast vervangen?</h2>
          <p className="mt-3 text-text-secondary">
            De totale prijs is opgebouwd uit materiaal (groepenkast, aardlekautomaten,
            hoofdschakelaar, bekabeling), arbeid en het verplichte NEN 1010-meetrapport.
            Hieronder onze richtprijzen inclusief btw voor een gemiddelde woning.
          </p>

          <div className="mt-5 overflow-x-auto rounded-2xl border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/50 text-foreground">
                <tr>
                  <th className="p-3">Situatie</th>
                  <th className="p-3">Richtprijs</th>
                  <th className="p-3">Doorlooptijd</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border text-text-secondary">
                <tr>
                  <td className="p-3">Groepenkast vervangen 1-fase (tot 6 groepen)</td>
                  <td className="p-3">€ 850 – € 1.250</td>
                  <td className="p-3">1 werkdag</td>
                </tr>
                <tr>
                  <td className="p-3">Groepenkast vervangen 3-fase (tot 9 groepen)</td>
                  <td className="p-3">€ 1.250 – € 1.850</td>
                  <td className="p-3">1 werkdag</td>
                </tr>
                <tr>
                  <td className="p-3">Verzwaren van 1×35A naar 3×25A (netbeheerder)</td>
                  <td className="p-3">€ 500 – € 900 (Liander/Stedin)</td>
                  <td className="p-3">2 – 6 weken</td>
                </tr>
                <tr>
                  <td className="p-3">Complete nieuwe meterkast + verzwaring + laadgroep</td>
                  <td className="p-3">€ 1.950 – € 2.750</td>
                  <td className="p-3">1 – 2 werkdagen</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-3 text-xs text-text-secondary">
            Genoemde prijzen zijn richtprijzen. U ontvangt altijd een schriftelijke offerte
            op basis van een intake ter plaatse of via foto's van de bestaande meterkast.
          </p>
        </section>

        <section className="mt-10">
          <h2 className="text-2xl font-bold text-foreground">Wanneer is meterkast vervangen nodig?</h2>
          <ul className="mt-3 space-y-2 text-text-secondary">
            <li className="flex gap-2"><Home className="mt-1 size-4 shrink-0 text-[var(--sky)]" aria-hidden /><span><strong>Elektrisch koken</strong> (inductie / perilex) — vaak een extra kookgroep en soms verzwaring naar 3-fase.</span></li>
            <li className="flex gap-2"><Zap className="mt-1 size-4 shrink-0 text-[var(--sky)]" aria-hidden /><span><strong>Laadpaal thuis</strong> — een aparte 16A of 32A laadgroep met eigen aardlekautomaat type B is verplicht.</span></li>
            <li className="flex gap-2"><Wrench className="mt-1 size-4 shrink-0 text-[var(--sky)]" aria-hidden /><span><strong>Warmtepomp of airco</strong> — verhoogt het gelijktijdige vermogen; 3-fase is bijna altijd nodig.</span></li>
            <li className="flex gap-2"><ShieldCheck className="mt-1 size-4 shrink-0 text-[var(--sky)]" aria-hidden /><span><strong>Verouderde kast</strong> zonder aardlekautomaten (ALS) — afgekeurd door taxateur of verzekeraar.</span></li>
            <li className="flex gap-2"><Home className="mt-1 size-4 shrink-0 text-[var(--sky)]" aria-hidden /><span><strong>Verbouwing of aanbouw</strong> — extra groepen, keuken- en badkamerscheiding.</span></li>
          </ul>
        </section>

        <section className="mt-10">
          <h2 className="text-2xl font-bold text-foreground">Zo helpt Elvion Group Nederland u verder</h2>
          <ol className="mt-3 list-decimal space-y-2 pl-6 text-text-secondary">
            <li>Gratis intake — telefonisch of via foto's van uw huidige meterkast.</li>
            <li>Vaste prijsopgave binnen 24 uur, inclusief materiaal, arbeid en meetrapport.</li>
            <li>Aanvraag verzwaring bij Liander, Stedin of Enexis (als 3-fase nodig is).</li>
            <li>Installatie door onze eigen NEN 1010-gecertificeerde monteurs.</li>
            <li>Oplevering met meetrapport, garantiebewijs en uitleg over de nieuwe kast.</li>
          </ol>
        </section>

        <section className="mt-10">
          <h2 className="text-2xl font-bold text-foreground">Veelgestelde vragen</h2>
          <div className="mt-4 divide-y divide-border rounded-2xl border border-border">
            {FAQS.map((f) => (
              <details key={f.q} className="group p-5">
                <summary className="cursor-pointer list-none font-bold text-foreground">
                  {f.q}
                </summary>
                <p className="mt-2 text-text-secondary">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="text-2xl font-bold text-foreground">Groepenkast vervangen in uw regio</h2>
          <p className="mt-3 text-text-secondary">
            Elvion Group Nederland plaatst en vervangt groepenkasten door heel Nederland. Bekijk onze lokale pagina's voor meer informatie over vervanging in uw stad.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            {[
              { label: "Rotterdam", slug: "rotterdam" },
              { label: "Utrecht", slug: "utrecht" },
              { label: "Tilburg", slug: "tilburg" },
              { label: "Nijmegen", slug: "nijmegen" },
            ].map((city) => (
              <Link
                key={city.slug}
                to="/groepenkast-vervangen/$stad"
                params={{ stad: city.slug }}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-4 py-2 text-sm font-semibold text-foreground hover:border-orange hover:text-orange"
              >
                {city.label}
                <ChevronRight className="h-3.5 w-3.5" />
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-10 rounded-2xl border border-border bg-card p-6 text-center shadow-sm">
          <h2 className="text-xl font-extrabold text-foreground">
            Meterkast laten vervangen door een erkende installateur?
          </h2>
          <p className="mt-2 text-text-secondary">
            Vraag vrijblijvend een vaste prijsopgave aan. Binnen 24 uur reactie.
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Button asChild size="lg">
              <Link to="/offerte-aanvragen" search={{ product: "groepenkast" }}>Offerte aanvragen</Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/klantenservice">Neem contact op</Link>
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
